import { AxiosResponse } from 'axios';

import { useMutation } from '@tanstack/react-query';

import { base64ToFile } from '../../utils';
import { axiosInstance } from '~/services/fetchServiceV2';
import { TypeErrorFormat, requestHelper } from '~/utils/fetchHelper';

/** @category useSubmitSpecialMedical */
export type TypeSubmitSpecialMedicalParams = {
  /** 클러스터 */
  cluster: string;
  /** 검진일자 */
  examinationDate: string;
  /** 파일 */
  file: string;
  /** 파일명 */
  fileName: string;
};

/** @category useSubmitSpecialMedical */
export type TypeSubmitSpecialMedicalResponse = {
  /** 응답 코드 */
  code: string;
  /** 응답 메시지 */
  message: string;
};

/**
 * @description 작업자의 특수건강검진 검진확인증 제출(등록 → 승인대기)
 */
export const useSubmitSpecialMedical = ({ isSecond = false }: { isSecond?: boolean } = {}) => {
  const createFormData = (cluster: string, examinationDate: string, file: string, fileName: string): FormData => {
    try {
      const formattedFile = base64ToFile(file, fileName);
      const formData = new FormData();
      formData.append('file', formattedFile);
      formData.append('filename', fileName);

      const jsonData = {
        cluster,
        examinationDate,
      };
      formData.append('data', new Blob([JSON.stringify(jsonData)], { type: 'application/json' }));

      return formData;
    } catch (e) {
      throw new Error('데이터 변환 실패', { cause: e });
    }
  };

  const { mutateAsync } = useMutation({
    mutationFn: (params: TypeSubmitSpecialMedicalParams) => {
      const endpoint = '/labor-accounts/examinations/special-medicals/submit';
      const secondEndpoint = '/labor-accounts/examinations/special-medicals/second/submit';

      const formData = createFormData(params.cluster, params.examinationDate, params.file, params.fileName);

      return axiosInstance.patch(isSecond ? secondEndpoint : endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
  });

  const requestSubmitSpecialMedical = ({
    params,
    onSuccess,
    onError,
    onFinally,
  }: {
    params: TypeSubmitSpecialMedicalParams;
    onSuccess?: (response: TypeSubmitSpecialMedicalResponse) => void;
    onError?: (error: TypeErrorFormat) => void;
    onFinally?: () => void;
  }) => {
    return requestHelper<TypeSubmitSpecialMedicalResponse>(
      () => mutateAsync(params) as Promise<AxiosResponse<TypeSubmitSpecialMedicalResponse>>,
      onSuccess,
      onError,
      onFinally
    );
  };

  return requestSubmitSpecialMedical;
};
